import {
	API_ENDPOINTS,
	DataTable,
	formatDate,
	FormInput,
	PageLayout,
	route,
	useLangContext,
	StringHelpers,
	Variants,
	FileHelpers,
	customToast,
	replaceKeyWithValue,
	Themes,
	getThemeFromUrl,
} from 'carrier-fe';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ResultType } from '../../types/result';

function TrainingResults() {
	const navigate = useNavigate();
	const { fields, crud } = useLangContext();
	const [searchParams] = useSearchParams();

	const isToshiba = getThemeFromUrl() === Themes.Toshiba;

	const handleOptionSelect = (action: any, data: ResultType) => {
		const { id = '', type } = data;

		const navRoute = type === 'course' ? 'result' : type;

		const actions: Record<string, () => void> = {
			view: () => {
				const searchFilter = searchParams.get('filter[search]');
				if (searchFilter) {
					sessionStorage.setItem('resultSearchFilter', searchFilter);
				}
				navigate(`/training/${navRoute}/${id}`);
			},
			download: () => downloadTrainingCertificate(id, type),
		};

		if (typeof action === 'string' && actions[action]) {
			actions[action]();
		}
	};

	const downloadTrainingCertificate = async (
		id: string,
		type: ResultType['type']
	) => {
		const url =
			type === 'course'
				? route(API_ENDPOINTS.TRAINING.RESULT.CERTIFICATE, {
						trainingResult: id,
					})
				: route(API_ENDPOINTS.TRAINING.EVENT.CERTIFICATE, {
						trainingEvent: id,
					});

		try {
			await FileHelpers.downloadFile(url, 'trainingCertificate.pdf');
		} catch (error: any) {
			customToast({
				variant: Variants.Danger,
				title:
					error?.response?.data?.message ||
					'Something went wrong how...',
			})
		}
	};

	/*
	 * Toshiba: cert is per-completed-course (type === 'course' && Passed).
	 * Other brands: cert stays per-attended-event (type === 'event' && Attended).
	 */
	const canDownloadCertificate = (data: ResultType): boolean =>
		isToshiba
			? data.type === 'course' && data.result === 'Passed'
			: data.type === 'event' && data.result === 'Attended';

	const colMapping = (data: ResultType) => {
		const cols: any = [
			{
				id: 'course_name',
				label: StringHelpers.title(
					fields?.course_name || 'Course Name'
				),
				value: StringHelpers.title(data.course_name || '-'),
				sortable: true,
			},
			{
				id: 'type',
				label: crud?.table_columns.type || 'Type',
				value: StringHelpers.title(data.type || '-'),
				sortable: true,
			},
			{
				id: 'category_name',
				label: StringHelpers.title(fields?.category || 'Category'),
				value: StringHelpers.title(data.category_name || '-'),
				sortable: true,
			},
			{
				id: 'completed_at',
				label: StringHelpers.title(fields?.result || 'Result'),
				value: StringHelpers.title(data.result || '-'),
				sortable: true,
			},
			{
				id: 'started_at',
				label: StringHelpers.title(fields?.started_at || 'Started'),
				value: formatDate(data.started_at) || '-',
				sortable: true,
			},
			{
				id: 'completed_at',
				label: StringHelpers.title(fields?.completed_at || 'Completed'),
				value: formatDate(data.completed_at) || '-',
				sortable: true,
			},
			{
				id: 'actions',
				label: StringHelpers.title(fields?.actions || 'Actions'),
				value: data.permissions.view ? (
					<FormInput
						type="select"
						label={StringHelpers.title(fields?.actions || 'Action')}
						placeholder={StringHelpers.title(
							fields?.select || 'Select'
						)}
						name="actions"
						value=""
						onChange={(value) => {
							if (!!value && typeof value === 'string') {
								handleOptionSelect(value, data);
							}
						}}
						options={[
							{
								label: StringHelpers.title(
									crud?.view || 'View'
								),
								value: 'view',
							},
							...(canDownloadCertificate(data) ? [{
								label: replaceKeyWithValue(
									crud?.buttons?.download?.default
										|| 'Download :any',
									'any',
									fields?.certificate
										|| 'Certificate'
								),
								value: 'download'
							}] : [])
						]}
						noMargin
					/>
				) : null,
			},
		];

		return cols;
	};

	return (
		<PageLayout title={fields?.completed_training || 'Completed Training'}>
			<DataTable<ResultType>
				routeEndpoint={route(API_ENDPOINTS.TRAINING.COMPLETED.INDEX)}
				colMapping={colMapping}
				incrementPaginationButtons
				canViewArchived={false}
				variant={Variants.Info}
			/>
		</PageLayout>
	);
}

export default TrainingResults;
